import React, { FC, useEffect, useState } from 'react'
import { View, useWindowDimensions, TouchableOpacity, FlatList, ActivityIndicator, RefreshControl, StyleSheet, Pressable, DeviceEventEmitter } from 'react-native'
import { useIsFocused } from '@react-navigation/native'
import Popover from 'react-native-popover-view/dist/Popover'

// Common component
import CommonHeader from '../../shared/commonComponents/commonHeader'
import CommonDialogBox from '../../shared/commonComponents/commonDialogBox'
import { appColors, appFonts, fontSize } from '../../themes/appTheme';
import { config } from '../../shared/environment'
import { getScreenPermission, useAppDispatch, useAppSelector } from '../../redux/hooks'
import { getAllPosManualTax, updateorDeleteTax } from '../../redux/slices/taxSlice'
import { TaxData, updatePropType, FilterPayloadType } from './taxModule'
import { taxListStyle } from '../../themes/components/tax/taxListStyle'
import { ZenFilterModal, ZenIcon, ZenBadge, ZenText, ZenSwitch, ZenEmptyComponent, ZenLoader, ZenToast } from 'zen-core-elements'
import { useAppNavigation } from '../../types/@react-navigation/navigation'
import { filterItemsProps } from 'zen-core-elements/src/elements/zenFilterModal/zenFilterType.modal';
import { ListToolbar } from '../../shared/commonComponents/listToolbar'
import { ThemeConsumer } from '@rneui/themed'
import { CategoryListStyle } from '../../themes/components/category/categoryListStyle'
import { shallowEqual } from 'react-redux'

const LIMIT = 10;

const TaxList: FC = () => {
    // Variable used to store Focused state
    const isFocused = useIsFocused()
    // Variable which is used for navigation
    const navigation = useAppNavigation()
    // redux dispatch
    const dispatch = useAppDispatch()
    const { isWrite: isTaxWrite } = useAppSelector(getScreenPermission("TaxStack"), shallowEqual)
    // Variable which is used to render styles based on device dimension
    const { width, height } = useWindowDimensions()

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [footerLoader, setFooterLoader] = useState<boolean>(false)
    const [refresh, setRefresh] = useState<boolean>(false)

    // Redux cached state
    const cachedRows = useAppSelector(state => state.tax.rows) || [];
    const cachedCount = useAppSelector(state => state.tax.count) || 0;
    const isReached = useAppSelector(state => state.tax.isReached);
    const filter = useAppSelector(state => state.tax.filter);
    const loading = useAppSelector(state => state.tax.loading);

    const [offset, setOffset] = useState<number>(0)
    const [searchText, setSearchText] = useState<string | null>(null);
    const [filterPayLoad, setFilterPayLoad] = useState<FilterPayloadType>({})

    // Variable which is used to store items
    const [item, setItem] = useState<any>(null)
    // Variable which is used to open/close delete popover
    const [deletePopover, setDeletePopover] = useState<boolean>(false)
    // Variable which is used to handle switch state
    const [updateSwitch, setupdateSwitch] = useState<boolean>(false)
    // Variable which is used to open/close filter
    const [filterVisible, setFilterVisible] = useState<boolean>(false)
    // Variable which is used to store filter count value
    const [filtercount, setFilterCount] = useState(0)

    const [selectedTax, setSelectedTax] = useState<number>(0)

    // Variable which is used to store filter data 
    const [filterData, setFilterData] = useState<filterItemsProps[]>([{ id: "1", filterName: "Medium", filterType: "radio", filterValue: [{ value: 'POS', label: 'POS' }, { value: 'WEB', label: 'WEB' }], initialSelected: '' }])

    //Variable used to store delete discount dialog props for deleting tax
    const deleteDialogProps = {
        content: `Are you sure you want to remove tax ${item?.name} ?`,
        button: [
            { id: 1, name: 'No', color: appColors.primary, backgroundColor: appColors.light },
            { id: 2, name: 'Yes', backgroundColor: appColors.primary, color: appColors.light },
        ],
        icon: { name: 'delete-outline', color: appColors.inActiveColor, type: 'material-community' }
    }

    //State variable used to store update tax dialogbox props
    const updateDialogProps = {
        content: `Do you want to ${item?.isEnabled ? 'disable' : 'enable'} this tax category?`,
        button: [{ id: 1, name: 'No', color: appColors.primary, backgroundColor: appColors.light }, { id: 2, name: 'Yes', color: appColors.light, backgroundColor: appColors.primary }],
        icon: { name: item?.isEnabled ? 'close-circle-outline' : 'check-circle-outline', color: item?.isEnabled ? appColors.darkRed : appColors.green, type: 'material-community' }
    }

    // useEffect hook which is used to call getAllTax function when page is focused
    useEffect(() => {
        if (isFocused) {
            getTaxAPI(0, '', {}, false);
        }
    }, [])

    // UseEffect hook Which is used to apply filter
    useEffect(() => {
        const currentFingerprint = JSON.stringify({ searchText: searchText ?? "", filterValue: JSON.stringify(filterPayLoad) });

        if (!refresh && filter !== currentFingerprint && filter !== null) {
            setOffset(0);
            getTaxAPI(0, searchText ?? "", filterPayLoad);
        }
    }, [filterPayLoad, filter, refresh]);

    // UseEffect used to search tax
    useEffect(() => {
        if (refresh) return;

        if (searchText) {
            setOffset(0)
            const debounceSearchText = setTimeout(() => {
                getTaxAPI(0, searchText, filterPayLoad, true);
            }, 1000);
            return () => clearTimeout(debounceSearchText);
        }
        else if (searchText == '') {
            getTaxAPI(0, searchText, filterPayLoad, true);
        }
    }, [searchText, refresh]);

    const getTaxAPI = (offsetVal: number, search: string, fp: FilterPayloadType, forceRefresh = false) => {
        const payload = {
            offset: offsetVal,
            limit: LIMIT,
            globalSearch: search,
            filterData: JSON.stringify(fp)
        };
        const currentFingerprint = JSON.stringify({ searchText: payload.globalSearch, filterValue: payload.filterData });

        if (!forceRefresh && offsetVal === 0 && filter === currentFingerprint) {
            setIsLoading(false);
            return;
        }

        dispatch(getAllPosManualTax({
            storeId: config?.storeId,
            data: payload
        })).unwrap().then(() => {
            setIsLoading(false)
            setFooterLoader(false)
            setRefresh(false)
        }).catch((err) => {
            setIsLoading(false)
            setFooterLoader(false)
            setRefresh(false)
            console.log("Error in get tax", err)
        })
    }

    const handleEndReached = () => {
        if (!isReached && !footerLoader && !isLoading && cachedRows.length < cachedCount) {
            const nextOffset = offset + LIMIT;
            setOffset(nextOffset);
            setFooterLoader(true);
            getTaxAPI(nextOffset, searchText ?? "", filterPayLoad);
        }
    };

    const handleRefresh = () => {
        setRefresh(true);
        setOffset(0);
        setFilterPayLoad({})
        DeviceEventEmitter.emit("Tax List Filter")
        setSearchText(null)
        getTaxAPI(0, '', {}, true);
    };

    // Method which is used to confirmation message for delete tax
    const confirmation = (value: { name: string }) => {
        if (value?.name == 'Yes' && deletePopover) {
            setDeletePopover(false)
            updateTax(item, 'delete')
        }
        else if (value?.name == 'Yes' && updateSwitch) {
            setupdateSwitch(false)
            updateTax(item, 'update')
        }
        else {
            setDeletePopover(false)
            setupdateSwitch(false)
            setItem(null)
            setSelectedTax(0)
        }
    }

    //Function to be called while removing a item from taxList
    const updateTax = async (taxItem: any, value: string) => {
        let payload: updatePropType
        if (value == 'update') {
            payload = { id: taxItem?.id, isEnabled: !taxItem?.isEnabled, storeId: config?.storeId, statusUpdateOnly: true }
        }
        else {
            setSearchText('')
            payload = { id: taxItem?.id, isDeleted: true, storeId: config?.storeId, statusUpdateOnly: true }
        }
        await dispatch(updateorDeleteTax({ storeId: config?.storeId, data: payload })).unwrap().then((res) => {
            if (res && res?.taxCategories) {
                ZenToast({ type: "success", message: value == 'update' ? `Tax ${taxItem?.isEnabled ? 'disabled' : 'enabled'} successfully` : `Tax removed successfully` })
                setItem(null)
                setSelectedTax(0)
            }
        }).catch((err) => {
            ZenToast({ type: "error", message: value == 'update' ? `Failed to ${taxItem?.isEnabled ? 'disable' : 'enable'} tax` : `Failed to remove tax` })
            console.log('Error in update tax', err)
        })
    }

    const renderItem = ({ item: rowItem, index }: { item: TaxData; index: number }) => {
        return (
            <ThemeConsumer>
                {({ theme }) => (
                    <View key={index} style={{ flexDirection: "row" }}>
                        <View style={{ flex: 1 }}>
                            <View style={{ padding: 10, marginHorizontal: 10, marginBottom: 10, borderRadius: 5, backgroundColor: theme.light }}>
                                <View style={{ flexDirection: "row", justifyContent: "space-between" }} >

                                    {/* Left Content Area containing Columns */}
                                    <View style={{ flexDirection: "row", flex: 1, alignItems: "center" }} >

                                        {/* Column 1: Tax Name & Status Badge */}
                                        <View style={{ flexDirection: "column", flex: 0.35, gap: 10 }}>
                                            <ZenText content={rowItem?.name ?? ''} numberOfLines={1} style={{ fontFamily: appFonts.bold, fontSize: fontSize.mediumFontSize, color: appColors.lightDark }} />
                                            <ZenBadge
                                                badgeColor={rowItem?.isEnabled ? "#e2f9e1" : "#ffebee"}
                                                badgeName={rowItem?.isEnabled ? "Enabled" : "Disabled"}
                                                badgeTextColor={rowItem?.isEnabled ? appColors.green : appColors.red}
                                                badgeTextSize="small"
                                                badgeTextStyle={{ fontFamily: theme.bold, padding: 2 }}
                                            />
                                        </View>

                                        {/* Column 2: Tax For */}
                                        <View style={{ flexDirection: "column", flex: 0.25, gap: 10 }}>
                                            <ZenText content="Tax For" numberOfLines={1} style={{ fontFamily: appFonts.medium, fontSize: fontSize.mediumFontSize, color: appColors.labelColor }} />
                                            <ZenText content={rowItem?.taxFor ?? ''} numberOfLines={1} style={{ fontFamily: appFonts.bold, fontSize: fontSize.smallerFontSize, color: appColors.lightDark }} />
                                        </View>

                                        {/* Column 3: Medium */}
                                        <View style={{ flexDirection: "column", flex: 0.2, gap: 10 }}>
                                            <ZenText content="Medium" numberOfLines={1} style={{ fontFamily: appFonts.medium, fontSize: fontSize.mediumFontSize, color: appColors.labelColor }} />
                                            <ZenText content={rowItem?.medium ?? ''} numberOfLines={1} style={{ fontFamily: appFonts.bold, fontSize: fontSize.smallerFontSize, color: appColors.lightDark }} />
                                        </View>

                                        {/* Column 4: Status Toggle */}
                                        <View style={{ flexDirection: "column", flex: 0.2, gap: 4, alignItems: 'flex-start' }}>
                                            <ZenText content="Status" numberOfLines={1} style={{ fontFamily: appFonts.medium, fontSize: fontSize.mediumFontSize, color: appColors.labelColor }} />
                                            <ZenSwitch
                                                activeColor={appColors.primary}
                                                inActiveColor={appColors.borderColor}
                                                activeThumbColor={theme.light}
                                                disabled={!isTaxWrite}
                                                inactiveThumbColor={theme.light}
                                                value={rowItem?.id == selectedTax ? !rowItem?.isEnabled : rowItem?.isEnabled}
                                                style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }], marginLeft: -8 }}
                                                onValueChange={() => {
                                                    setSelectedTax(rowItem?.id)
                                                    setItem(rowItem)
                                                    setupdateSwitch(true)
                                                }}
                                            />
                                        </View>

                                    </View>

                                    {/* Right Action Icons Area */}
                                    <View style={{ flexDirection: "row", alignItems: "center", gap: 10, alignSelf: "center" }}>
                                        <TouchableOpacity onPress={() => navigation.navigate("AddEditTax", { ele: rowItem, isView: true })}>
                                            <ZenIcon
                                                name="eye"
                                                type="octicon"
                                                color={theme.iconColor}
                                                size={theme.iconSize}
                                                style={CategoryListStyle?.iconStyle}
                                            />
                                        </TouchableOpacity>

                                        {isTaxWrite && <TouchableOpacity onPress={() => navigation.navigate("AddEditTax", { ele: rowItem, isView: false })}>
                                            <ZenIcon
                                                name="pencil"
                                                type="octicon"
                                                color={theme.iconColor}
                                                size={theme.iconSize}
                                                style={CategoryListStyle?.iconStyle}
                                            />
                                        </TouchableOpacity>}

                                        {isTaxWrite && <TouchableOpacity onPress={() => { setItem(rowItem); setDeletePopover(true); }}>
                                            <ZenIcon
                                                name="delete-outline"
                                                type="material-community"
                                                color={appColors.red}
                                                size={theme.iconSize}
                                                style={CategoryListStyle?.iconStyle}
                                            />
                                        </TouchableOpacity>}
                                    </View>

                                </View>
                            </View>
                        </View>
                    </View>
                )}
            </ThemeConsumer>
        );
    }

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <View style={taxListStyle.container}>
                    <CommonHeader headerName="Taxes" />
                    <ListToolbar
                        search={{
                            value: searchText ?? "",
                            onChange: (val) => setSearchText(val as string),
                            onCancel: () => setSearchText(null),
                            placeholder: 'Search by Tax Name',
                        }}
                        filter={{
                            count: filtercount ?? 0,
                            onPress: () => setFilterVisible(true),
                        }}
                        buttons={isTaxWrite ? [{
                            onPress: () => navigation.navigate("AddEditTax"),
                            title: "Create Manual Tax",
                            variant: "primary"
                        }] : []}
                    />

                    {(loading && !offset && (!refresh || cachedRows.length === 0)) ? <ZenLoader lottieType="Local" lottie={require("../../assets/infinityLoader.json")} /> :
                        <View style={{ flex: 1, paddingTop: 10 }}>
                            <FlatList
                                data={cachedRows}
                                keyExtractor={(_, index) => index?.toString()}
                                renderItem={renderItem}
                                contentContainerStyle={styles.listContent}
                                onEndReached={handleEndReached}
                                onEndReachedThreshold={0.5}
                                refreshControl={
                                    <RefreshControl refreshing={refresh} onRefresh={handleRefresh} colors={[appColors.primary]} />
                                }
                                ListFooterComponent={
                                    footerLoader ? (
                                        <ActivityIndicator size="small" color={appColors.primary} style={{ paddingVertical: 12 }} />
                                    ) : null
                                }
                                ListEmptyComponent={ZenEmptyComponent}
                            />
                        </View>
                    }
                    <ZenFilterModal
                        setVisible={setFilterVisible}
                        visible={filterVisible}
                        filterName="Tax List Filter"
                        filterItems={filterData}
                        setFilterItems={setFilterData}
                        filterCount={filtercount}
                        setFilterCount={setFilterCount}
                        isSingleExpand={false}
                        title='Filter'
                        onConfirm={(val) => {
                            let data: FilterPayloadType = {}
                            if (val?.values?.["Medium"]) {
                                data["medium"] = val?.values?.["Medium"]
                            }
                            setIsLoading(true);
                            setFilterPayLoad(data)
                        }}
                    />

                    <Popover isVisible={deletePopover} popoverStyle={{ width: width > 900 ? width / 2 : width > 600 ? 450 : 350, borderRadius: 10 }}>
                        <CommonDialogBox type="OTHER" dialog={deleteDialogProps} confirmation={confirmation} />
                    </Popover>
                    <Popover isVisible={updateSwitch} popoverStyle={{ width: width > 900 ? width / 2 : width > 600 ? 450 : 350, borderRadius: 10 }}>
                        <CommonDialogBox type="OTHER" dialog={updateDialogProps} confirmation={confirmation} />
                    </Popover>
                    {
                        width < height && isTaxWrite &&
                        <Pressable
                            style={theme?.staffListStyle?.fabButton}
                            onPress={() => navigation.navigate("AddEditTax")}>
                            <ZenIcon type="material-community" name={"plus"} size={25} color={theme?.light} />
                        </Pressable>
                    }
                </View>
            )}
        </ThemeConsumer>
    )
}

const styles = StyleSheet.create({
    listContent: {
        paddingBottom: 20,
        flexGrow: 1,
    }
});

export default TaxList